import React, { useState } from 'react'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { Dialog, DialogContent, DialogFooter } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { useBooksStore } from '../../store/booksStore'
import { BookOpen, Plus, Upload, Archive, MoreHorizontal, ArrowRight, Download, Trash2, HardDrive, FileText } from 'lucide-react'
import MetadataWizard, { type BatchWitnessMetadata } from '../home/MetadataWizard'
import { analyzeFiles, type FileAnalysis } from '../../lib/smartDropHandler'
import { extractTextContent } from '../../lib/documentExtractor'
import { exportImportManager } from '../../lib/exportImportManager'
import { API_ENDPOINTS } from '../../config/api'
import type { ImportOptions } from '../../types/exportTypes'

interface BookCardProps {
  book: any
  onOpenBook: (bookId: string) => void
  onExportBook: (bookId: string) => void
  onDeleteBook: (bookId: string) => void
}

function BookCard({ book, onOpenBook, onExportBook, onDeleteBook }: BookCardProps) {
  const { getBookChapters, getChapterWitnesses } = useBooksStore()
  const [showMenu, setShowMenu] = useState(false)

  const chapters = getBookChapters(book.id)
  const totalWitnesses = chapters.reduce((total, chapter) => total + getChapterWitnesses(chapter.id).length, 0)

  const completedWitnesses = chapters.reduce((total, chapter) => {
    const witnesses = getChapterWitnesses(chapter.id)
    return total + witnesses.filter((w) => w.processing_status === 'completed').length
  }, 0)

  const processingWitnesses = chapters.reduce((total, chapter) => {
    const witnesses = getChapterWitnesses(chapter.id)
    return total + witnesses.filter((w) => ['preprocessing', 'preprocessed', 'segmenting', 'auto-processing', 'paused'].includes(w.processing_status)).length
  }, 0)

  const bookStatus: 'complete' | 'processing' | 'pending' = (() => {
    if (totalWitnesses > 0 && completedWitnesses === totalWitnesses) return 'complete'
    if (processingWitnesses > 0) return 'processing'
    return 'pending'
  })()

  // Matching WitnessCard styleMap
  const styleMap = {
    complete: {
      stripe: 'bg-green-500',
      progress: 'bg-green-600',
      text: 'text-green-700'
    },
    processing: {
      stripe: 'bg-yellow-500',
      progress: 'bg-yellow-600',
      text: 'text-yellow-700'
    },
    pending: {
      stripe: 'bg-gray-300',
      progress: 'bg-gray-500',
      text: 'text-gray-500'
    }
  }[bookStatus]

  const progress = totalWitnesses > 0 ? Math.round((completedWitnesses / totalWitnesses) * 100) : 0

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (window.confirm(`Are you sure you want to delete "${book.name}"? This will permanently remove all chapters, witnesses, and analysis results.`)) {
      onDeleteBook(book.id)
    }
    setShowMenu(false)
  }

  const handleExport = (e: React.MouseEvent) => {
    e.stopPropagation()
    onExportBook(book.id)
    setShowMenu(false)
  }

  return (
    <Card
      className="bg-white border border-black shadow-sm cursor-pointer hover:shadow-md transition-all relative overflow-hidden h-full flex flex-col"
      onClick={() => onOpenBook(book.id)}
    >
      {/* Vertical status stripe on left */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${styleMap.stripe}`} />

      {/* Header: Title + Description */}
      <div className="p-3 pl-4 flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate flex items-center gap-2">
            <Archive className="h-3.5 w-3.5 text-gray-400" />
            {book.name}
          </div>
          {book.description && (
            <p className="text-[11px] text-gray-500 truncate ml-5.5">{book.description}</p>
          )}
        </div>

        {/* Open Button (Action) */}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs shrink-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
          onClick={(e) => {
            e.stopPropagation()
            onOpenBook(book.id)
          }}
        >
          Open
          <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </div>

      {/* Progress bar */}
      <div className="mx-3 ml-4 h-1 bg-gray-200">
        <div className={`h-full ${styleMap.progress}`} style={{ width: `${progress}%` }} />
      </div>

      {/* Footer: status + stats + menu */}
      <div className="px-3 pl-4 py-2 text-[11px]">
        {!showMenu ? (
          <div className="flex items-center justify-between">
            <span className={`${styleMap.text} font-medium`}>
              {bookStatus === 'complete' ? 'Completed' : bookStatus === 'processing' ? 'Processing' : 'Pending'}
            </span>
            <div className="flex items-center gap-3 text-gray-400">
              <span>{chapters.length} ch</span>
              <span>{totalWitnesses} wit</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-1 hover:text-gray-700"
                onClick={(e) => { e.stopPropagation(); setShowMenu(true) }}
              >
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1 text-gray-500 hover:text-gray-700"
              onClick={(e) => { e.stopPropagation(); setShowMenu(false) }}
            >
              ←
            </Button>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-2 text-[10px] text-gray-600 hover:bg-gray-100"
                onClick={handleExport}
              >
                <Download className="h-3 w-3 mr-1" />
                Export
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-2 text-[10px] text-red-600 hover:bg-red-50"
                onClick={handleDelete}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

interface NewBookCardProps {
  onSmartImport: (files: FileList) => void
}

function NewBookCard({ onSmartImport }: NewBookCardProps) {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onSmartImport(e.dataTransfer.files)
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onSmartImport(e.target.files)
      // Reset input value to allow selecting same file again
      e.target.value = ''
    }
  }

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        multiple
        accept=".json,.txt,.docx,.md"
        onChange={handleFileChange}
      />
      <div
        className={`bg-gray-50 border border-black border-dashed h-full flex flex-col items-center justify-center cursor-pointer group hover:bg-gray-100 transition-colors ${isDragging ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-200 ring-inset' : ''
          }`}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className={`w-full h-full flex flex-col items-center justify-center p-4 gap-2 ${isDragging ? 'pointer-events-none' : ''}`}
        >
          <div className={`h-9 w-9 rounded-full border flex items-center justify-center shadow-sm transition-all group-hover:-translate-y-0.5 ${isDragging ? 'bg-blue-100 border-blue-300' : 'bg-white border-black/10'}`}>
            {isDragging ? (
              <Upload className="h-5 w-5 text-blue-600" />
            ) : (
              <Plus className="h-5 w-5 text-gray-600 group-hover:text-black" />
            )}
          </div>
          <span className={`text-sm font-medium ${isDragging ? 'text-blue-700' : 'text-gray-600 group-hover:text-black'}`}>
            {isDragging ? 'Drop files to import' : 'Create / Import'}
          </span>
        </div>
      </div>
    </>
  )
}

export default function BooksDashboard() {
  const {
    books,
    loading,
    error,
    createBook,
    deleteBook,
    setCurrentBook,
    createChapter,
    createWitness,
    dbInitialized,
    migrateFromLocalStorage,
    getStorageUsage,
    getBookChapters,
    getChapterWitnesses,
    initializeDB
  } = useBooksStore()

  const [showMigrationDialog, setShowMigrationDialog] = React.useState(false)
  const [migrationStatus, setMigrationStatus] = React.useState<string>('')
  const [storageInfo, setStorageInfo] = React.useState<{ used: number; estimatedPercentage: number } | null>(null)

  // Smart Import / Wizard State
  const [wizardOpen, setWizardOpen] = useState(false)
  const [fileAnalyses, setFileAnalyses] = useState<FileAnalysis[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingStatus, setProcessingStatus] = useState('')


  // Check for localStorage data that needs migration
  React.useEffect(() => {
    if (dbInitialized) {
      const hasLocalStorageData = localStorage.getItem('books_index') !== null
      if (hasLocalStorageData) {
        setShowMigrationDialog(true)
      }

      // Load storage usage info
      getStorageUsage().then(setStorageInfo).catch(console.error)
    }
  }, [dbInitialized, getStorageUsage])

  const totalChapters = React.useMemo(() => {
    return books.reduce((sum, book) => sum + getBookChapters(book.id).length, 0)
  }, [books, getBookChapters])

  const totalWitnesses = React.useMemo(() => {
    return books.reduce((sum, book) => {
      const chapters = getBookChapters(book.id)
      const witnesses = chapters.reduce((chapterSum, chapter) => chapterSum + getChapterWitnesses(chapter.id).length, 0)
      return sum + witnesses
    }, 0)
  }, [books, getBookChapters, getChapterWitnesses])

  const handleMigration = async () => {
    setMigrationStatus('Migrating...')
    try {
      const result = await migrateFromLocalStorage()
      if (result.success) {
        setMigrationStatus(`Migration successful! Migrated ${result.migrated} items.`)
        setTimeout(() => {
          setShowMigrationDialog(false)
          setMigrationStatus('')
        }, 2000)
      } else {
        setMigrationStatus(`Migration completed with errors. Check console for details.`)
      }
    } catch (error) {
      setMigrationStatus(`Migration failed: ${error}`)
    }
  }

  const handleCreateBook = async (name: string, description?: string) => {
    try {
      const bookId = await createBook(name, description)
      if (bookId) {
        setCurrentBook(bookId)
        window.location.hash = `#/books/${bookId}`
      }
    } catch (error) {
      console.error('Failed to create book:', error)
    }
  }

  const handleOpenBook = (bookId: string) => {
    setCurrentBook(bookId)
    window.location.hash = `#/books/${bookId}`
  }

  const handleExportBook = (bookId: string) => {
    void (async () => {
      try {
        const book = books.find(b => b.id === bookId)
        const exportData = await exportImportManager.exportBook(bookId, 'json')
        const blob = typeof exportData === 'string'
          ? new Blob([exportData], { type: 'application/json' })
          : exportData
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${book?.name.replace(/[^a-z0-9]/gi, '_')}_export_${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Failed to export book:', error)
        alert(`Failed to export book: ${error instanceof Error ? error.message : String(error)}`)
      }
    })()
  }

  // --- Logic ported from HomePage.tsx ---

  const handleSmartImport = async (files: FileList) => {
    const analyses = await analyzeFiles(files)

    if (analyses.length === 0) return

    // Filter for raw text files
    const rawTextFiles = analyses.filter((a) => a.type === 'raw-text')

    if (rawTextFiles.length > 0) {
      // Show wizard for batch metadata collection
      setFileAnalyses(rawTextFiles)
      setWizardOpen(true)
      return
    }

    // Handle JSON files
    const jsonFiles = analyses.filter((a) => a.type.endsWith('-json'))
    if (jsonFiles.length > 0) {
      await handleJsonImport(jsonFiles)
      return
    }

    console.warn('No recognized file types')
  }

  const handleJsonImport = async (jsonFiles: FileAnalysis[]) => {
    try {
      for (const jsonFile of jsonFiles) {
        console.log(`Importing ${jsonFile.file.name}...`)
        const content = await jsonFile.file.text()

        const options: ImportOptions = {
          merge_strategy: 'merge',
          conflict_resolution: 'auto_resolve',
          preserve_ids: false,
          validate_integrity: true,
          import_references_only: false,
        }

        const result = await exportImportManager.importData(content, options)

        if (result.success) {
          // Refresh UI
          await initializeDB()
          console.log(`Successfully imported ${result.imported_items.length} items from ${jsonFile.file.name}`)
          alert(`Imported ${jsonFile.file.name} successfully!`)
        } else {
          console.error(`Import failed for ${jsonFile.file.name}:`, result.errors)
          alert(`Failed to import ${jsonFile.file.name}: ${result.errors.map((e) => e.message).join(', ')}`)
        }
      }
    } catch (error) {
      console.error('JSON import error:', error)
      alert(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const autoPreprocessText = async (text: string, filename: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.preprocessingAuto, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          filename,
        }),
      })

      if (!response.ok) {
        throw new Error(`Preprocessing failed: ${response.status}`)
      }

      const result = await response.json()
      return result
    } catch (error) {
      console.error('Preprocessing error:', error)
      return null
    }
  }

  const handleWizardSubmit = async (metadata: BatchWitnessMetadata) => {
    if (fileAnalyses.length === 0) return

    setIsProcessing(true)
    setProcessingStatus('Creating book and chapter...')

    try {
      // Create Book
      const bookId = await createBook(metadata.bookTitle)

      // Create Chapter
      const chapterId = await createChapter(
        bookId,
        metadata.chapterLatin,
        `${metadata.chapterLatin} | ${metadata.chapterArabic}`
      )

      setProcessingStatus(`Preparing ${metadata.witnesses.length} witnesses...`)

      // Create all witnesses in batch with preprocessing
      for (let i = 0; i < metadata.witnesses.length; i++) {
        const witnessData = metadata.witnesses[i]

        setProcessingStatus(`Preprocessing ${witnessData.siglum} (${i + 1}/${metadata.witnesses.length})...`)

        // Extract text content (supports .txt, .docx, .rtf)
        const content = await extractTextContent(witnessData.file)

        // CRITICAL: Preprocess the text to extract chunks with trigger words
        console.log(`Preprocessing ${witnessData.siglum}...`)
        const preprocessingResult = await autoPreprocessText(content, witnessData.file.name)

        if (!preprocessingResult) {
          console.error(`Failed to preprocess ${witnessData.siglum}, skipping...`)
          continue
        }

        // Create TextFile object
        const uploadedFile = {
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: witnessData.file.name,
          content,
          size: content.length,
          uploadedAt: Date.now(),
        }

        // Create Witness with preprocessing result
        await createWitness(chapterId, {
          sigla: witnessData.siglum,
          description: witnessData.description || witnessData.siglum,
          library: witnessData.library || '',
          uploaded_file: uploadedFile,
          preprocessing_result: preprocessingResult,
          last_modified: Date.now(),
          version: 1,
        })

        console.log(`Created witness ${witnessData.siglum}`)
      }

      // Close wizard
      setWizardOpen(false)
      setFileAnalyses([])
      setIsProcessing(false)
      setProcessingStatus('')

      // Navigate to the new book
      setCurrentBook(bookId)
      window.location.hash = `#/books/${bookId}`

    } catch (error) {
      console.error('Failed to create witnesses:', error)
      setIsProcessing(false)
      setProcessingStatus('')
      alert('Failed to process one or more files.')
    }
  }


  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-muted-foreground">Loading books...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8 relative">
      {/* Visual Alignment: Mockup-style header toolbar matched to ChapterView */}
      <div className="bg-white border border-black shadow-sm mb-4">
        {/* Row 1: Title/Breadcrumb Context */}
        <div className="px-4 py-2 border-b border-black/10 flex items-center justify-between">
          <div className="flex items-center gap-1 text-sm font-semibold">
            <span>Scholarly Text Analysis</span>
            <span className="text-gray-400">/</span>
            <span>Books</span>
          </div>
          {/* Storage Info Widget (Small) */}
          {storageInfo && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <HardDrive className="h-3 w-3" />
              <span>{(storageInfo.used / 1024 / 1024).toFixed(1)} MB used</span>
            </div>
          )}
        </div>

        {/* Row 2: Stats (No Actions) */}
        <div className="px-4 py-2 flex items-center justify-between flex-wrap gap-2">
          {/* Stats */}
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-baseline gap-1.5">
              <span className="font-semibold text-blue-700">{books.length}</span>
              <span className="text-xs text-gray-500">Books</span>
            </div>
            <div className="h-3 border-l border-black/20" />
            <div className="flex items-baseline gap-1.5">
              <span className="font-semibold text-blue-700">{totalChapters}</span>
              <span className="text-xs text-gray-500">Chapters</span>
            </div>
            <div className="h-3 border-l border-black/20" />
            <div className="flex items-baseline gap-1.5">
              <span className="font-semibold text-blue-700">{totalWitnesses}</span>
              <span className="text-xs text-gray-500">Witnesses</span>
            </div>
          </div>
        </div>
      </div>

      {/* Migration Dialog */}
      {showMigrationDialog && (
        <div className="mb-6 p-5 bg-blue-50 border border-black rounded-none shadow-sm">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Data Migration Available</h3>
          <p className="text-blue-800 mb-4">
            We found data from the previous localStorage system. Migrate it to IndexedDB for higher capacity and reliability.
          </p>
          {migrationStatus ? (
            <div className="text-blue-700 font-medium">{migrationStatus}</div>
          ) : (
            <div className="flex space-x-2">
              <Button className="matrix-compact-btn rounded-none shadow-sm" onClick={handleMigration}>
                Migrate to IndexedDB
              </Button>
              <Button variant="outline" className="matrix-compact-btn rounded-none" onClick={() => setShowMigrationDialog(false)}>
                Maybe Later
              </Button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-black rounded-none">
          <p className="text-destructive">{error}</p>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* New Book Card - Always visible as the first item */}
        <NewBookCard
          onSmartImport={handleSmartImport}
        />

        {books.map((book) => (
          <BookCard
            key={book.id}
            book={book}
            onOpenBook={handleOpenBook}
            onExportBook={handleExportBook}
            onDeleteBook={deleteBook}
          />
        ))}
      </div>

      {/* Wizard & Processing Overlay */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <div className="text-lg font-medium">{processingStatus}</div>
          </div>
        </div>
      )}

      <MetadataWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSubmit={handleWizardSubmit}
        fileAnalyses={fileAnalyses}
      />
    </div>
  )
}
